import { storage } from '@innovamat/radiance-utils';
import axios from 'axios';
import { gql, GraphQLClient } from 'graphql-request';
import type { Credentials } from '../types/credentials';
import type {
  CleverTokenResponse,
  LoginRequest,
  LoginResponse,
} from '../types/login';
import { AuthError } from './AuthError';

const BASE_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json;charset=utf-8',
};

export const usersLoginRequest = async (
  request: LoginRequest,
  credentials: Credentials
): Promise<LoginResponse> => {
  try {
    const url = `${credentials.apiUsersUrl}/auth/openid-connect/token`;

    const body = JSON.stringify({
      grant_type: 'password',
      client_id: credentials.usersClientId,
      username: request.username,
      password: request.password,
      scope: 'openid profile email',
    });

    const response = await fetch(url, {
      method: 'POST',
      body,
      headers: { ...BASE_HEADERS },
    });

    const data = await response.json();

    if (response.ok) return data;

    throw new AuthError(data);
  } catch (error: any) {
    throw new AuthError({
      title: error.message,
      detail: error.stack,
    });
  }
};

export const usersLoginRequestByAccessToken = async (
  accessToken: string,
  credentials: Credentials
): Promise<LoginResponse> => {
  try {
    const url = `${credentials.apiUsersUrl}/auth/openid-connect/token`;

    const body = JSON.stringify({
      grant_type: 'access_token',
      client_id: credentials.usersClientId,
      token: accessToken,
      scope: 'openid profile email',
    });

    const response = await fetch(url, {
      method: 'POST',
      body,
      headers: { ...BASE_HEADERS },
    });

    const data = await response.json();

    if (response.ok) return data;

    throw new AuthError(data);
  } catch (error: any) {
    throw new AuthError({
      title: error.message,
      detail: error.stack,
    });
  }
};

export const fromExternalLoginRequest = async (
  request: string,
  grantType: 'azure_token' | 'google_token' | 'clever_token',
  credentials: Credentials
): Promise<LoginResponse> => {
  try {
    const url = `${credentials.apiUsersUrl}/auth/openid-connect/token`;
    const body = JSON.stringify({
      grant_type: grantType,
      client_id: credentials.usersClientId,
      token: request,
    });

    const response = await fetch(url, {
      method: 'POST',
      body,
      headers: { ...BASE_HEADERS },
    });

    const data = await response.json();

    if (response.ok) return data;

    throw new AuthError(data);
  } catch (error: any) {
    throw new AuthError({
      title: error.message,
      detail: error.stack,
    });
  }
};

export const cleverLoginRequest = async (
  code: string,
  redirectTo: string,
  credentials: Credentials
) => {
  const graphQLClient = new GraphQLClient(credentials.apolloUrl);
  const { signClever } = await graphQLClient.request<{
    signClever: CleverTokenResponse;
  }>(
    gql`
      query signClever($code: String!, $redirectTo: String!) {
        signClever(code: $code, redirectTo: $redirectTo) {
          access_token
          token_type
        }
      }
    `,
    { code, redirectTo }
  );
  return signClever;
};

export const checkSessionRequest = async (
  apiUsersUrl: string,
  accessToken: string
): Promise<void> => {
  try {
    const url = `${apiUsersUrl}/auth/session/check`;
    await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: 5000,
    });
    return;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status !== 403) return;
    if (data) throw new AuthError(data);
    throw new AuthError({
      title: 'Revoked access token',
      status,
    });
  }
};

export const logoutSessionRequest = async (
  apiUsersUrl: string,
  accessToken: string
): Promise<void> => {
  const deviceCode = storage.device.get() || '';
  try {
    const url = `${apiUsersUrl}/auth/session/logout`;
    await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        deviceCode,
      }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return;
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (data) throw new AuthError(data);
    throw new AuthError({
      title:
        status === 405
          ? 'Invalid token or already logged out'
          : 'Internal server error',
      status,
    });
  }
};
